import { useMemo, useState } from 'react'
import Input from './Input'
import { ui } from '../../styles/ui'

function normalizeCurrency(value) {
  return String(value ?? '').trim().toUpperCase()
}

export default function SearchableCurrencySelect({
  id,
  value,
  options = [],
  onChange,
  emptyLabel = 'Select currency',
  searchPlaceholder = 'Search currency',
  searchAriaLabel = 'Search currency',
  className = '',
  searchClassName = '',
  selectClassName = '',
  disabled = false,
}) {
  const [search, setSearch] = useState('')

  const normalizedValue = normalizeCurrency(value)

  const normalizedOptions = useMemo(() => {
    const seen = new Set()
    const next = []
    for (const option of options) {
      const currency = normalizeCurrency(option)
      if (!currency || seen.has(currency)) continue
      seen.add(currency)
      next.push(currency)
    }
    if (normalizedValue && !seen.has(normalizedValue)) {
      next.unshift(normalizedValue)
    }
    return next
  }, [options, normalizedValue])

  // WHY: currency catalogs are now large enough that every picker needs inline search.
  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return normalizedOptions

    const matches = normalizedOptions.filter((currency) => (
      currency.toLowerCase().includes(query)
    ))

    if (normalizedValue && !matches.includes(normalizedValue)) {
      matches.unshift(normalizedValue)
    }

    return matches
  }, [normalizedOptions, normalizedValue, search])

  function handleChange(event) {
    const nextValue = normalizeCurrency(event.target.value)
    onChange(nextValue)
    setSearch('')
  }

  return (
    <div className={`space-y-2 ${className}`.trim()}>
      <Input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchAriaLabel}
        className={searchClassName}
        disabled={disabled}
      />
      <select
        id={id}
        value={normalizedValue}
        onChange={handleChange}
        disabled={disabled}
        className={selectClassName || ui.forms.select}
      >
        {emptyLabel ? <option value="">{emptyLabel}</option> : null}
        {filteredOptions.map((currency) => (
          <option key={currency} value={currency}>{currency}</option>
        ))}
      </select>
    </div>
  )
}
